import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import Icon from '../../../utils/icons';
import I18n from '../../../localization';
import { IQ_AIR_CONTACT_SUPPORT } from '../../../utils/constant';
import HCServices from '../../../services/hcServices';
import { buildWiringPayload, normalizeWiringMap, adjustMapForDisplayType } from './wiringUtils';

const TERMINAL_MAPPING = {
    "W or W1": "W",
    "Y or Y1": "Y",
    "O/B": "OB",
    "G or F": "G",
    "S1 or S2 or S3": "S",
};

const normalizeTerminal = (t) => TERMINAL_MAPPING[t] || t;

const WIRE_COLORS = [
    { name: 'White', hex: '#FFFFFF' },
    { name: 'Red', hex: '#E02020' },
    { name: 'Green', hex: '#14C415' },
    { name: 'Yellow', hex: '#F5C518' },
    { name: 'Blue', hex: '#2979FF' },
    { name: 'Brown', hex: '#8B4513' },
    { name: 'Orange', hex: '#F37320' },
    { name: 'Black', hex: '#333333' },
    { name: 'Purple', hex: '#9C27B0' },
];

// Power Plate flow step constants
export const PP_STEPS = {
    PP_REQUIRED: 'PP_REQUIRED',
    PP_WHAT_IS: 'PP_WHAT_IS',
    PP_INSTALLING: 'PP_INSTALLING',
    PP_WHATS_IN_BOX: 'PP_WHATS_IN_BOX',
    PP_TOOLS_NEEDED: 'PP_TOOLS_NEEDED',
    PP_SELECT_COLORS: 'PP_SELECT_COLORS',
    PP_REMOVE_THERMOSTAT: 'PP_REMOVE_THERMOSTAT',
    PP_INSTALL_TRIM: 'PP_INSTALL_TRIM',
    PP_MOUNT_PLATE: 'PP_MOUNT_PLATE',
    PP_CONNECT_WIRES: 'PP_CONNECT_WIRES',
    PP_GOTO_HVAC: 'PP_GOTO_HVAC',
    PP_OPEN_HVAC: 'PP_OPEN_HVAC',
    PP_TERMINAL_STRIP: 'PP_TERMINAL_STRIP',
    PP_CONNECT_C: 'PP_CONNECT_C',
    PP_CONNECT_C_2: 'PP_CONNECT_C_2',
    PP_MOUNT_BASE: 'PP_MOUNT_BASE',
    PP_INSTALL_CABLE: 'PP_INSTALL_CABLE',
    PP_CLOSE_PANEL: 'PP_CLOSE_PANEL',
    PP_ATTACH_COVER: 'PP_ATTACH_COVER',
    PP_RETURN_PLATE: 'PP_RETURN_PLATE',
};

// All No C-Wire step keys (UW + PP) for dispatching from InstallationGuide
export const NO_C_WIRE_STEPS = new Set([
    'POWER_PLATE',
    'UW_TAKE_PHOTO', 'UW_GOTO_HVAC', 'UW_OPEN_HVAC', 'UW_TERMINAL_STRIP',
    'UW_FIND_WIRE_COLOR', 'UW_COMMON_TERMINAL', 'UW_COMMON_TERMINAL_2', 'UW_CLOSE_RETURN',
    ...Object.values(PP_STEPS),
]);

const NoCWireFlow = ({
    currentStep,
    classes,
    goTo,
    goBack,
    renderStepper,
    renderImage,
    renderBottomContinue,
    renderYesNo,
    selectedTerminals,
    wiringMap,
    setWiringMap,
    unusedWirePhoto,
    setUnusedWirePhoto,
    unusedWirePhotoName,
    setUnusedWirePhotoName,
    capturePhoto,
    uploadPhoto,
    handleFileSelect,
    handleUnusedWireComplete,
    isDarkMode,
    setIsPowerPlateFlow,
    STEPS,
    displayType,
    isDualRNoC,
    openPhoto,
    renderPhotoOverlay,
    ppWireColors,
    setPpWireColors,
    ppCustomColors,
    setPpCustomColors,
    openURL,
}) => {
    const [openDropdown, setOpenDropdown] = useState(null);

    // ── Helpers ────────────────────────────────────────────────────────────────
    const getPpTerminals = () => {
        const normalized = selectedTerminals.map(normalizeTerminal);
        const hasR = normalized.some(t => ['R', 'Rh', 'Rc'].includes(t));
        const rLabel = hasR
            ? (normalized.includes('Rh') ? 'Rh' : normalized.includes('Rc') ? 'Rc' : 'R')
            : 'R';

        // For power plate: second terminal is the cooling terminal (Y or Y2),
        // or heating terminal (W or W2) if no cooling terminal was selected
        const coolingTerminal = normalized.includes('Y') ? 'Y'
            : normalized.includes('Y2') ? 'Y2'
            : null;
        const heatingTerminal = normalized.includes('W') ? 'W'
            : normalized.includes('W2') ? 'W2'
            : null;
        const secondTerminal = coolingTerminal || heatingTerminal;

        const terminals = [rLabel];
        if (secondTerminal) terminals.push(secondTerminal);
        return terminals;
    };

    const [ppWiringMapState, setPpWiringMapState] = useState([]);

    useEffect(() => {
        const fetchPpWiringMap = async () => {
            try {
                const payload = buildWiringPayload([...selectedTerminals, 'C']);
                const response = await HCServices.validateWiring(payload);
                if (response.status === 'failure') { return; }
                const data = response.data || response;
                const validationStatus = data['validation-status'];
                if (validationStatus && validationStatus !== 'VALID' && validationStatus !== 'VALID_NO_C_WIRE' && validationStatus !== 'VALID_NO_C_WIRE_DUAL_R') { return; }
                const map = adjustMapForDisplayType(normalizeWiringMap(data['wiring-map'] || []), displayType);
                if (map) setPpWiringMapState(map);
            } catch (error) {
                console.error('PP wiring map fetch failed:', error);
            }
        };
        fetchPpWiringMap();
    }, [selectedTerminals, displayType]);

    const getColorForTerminal = (terminal) => {
        const color = ppWireColors[terminal];
        if (!color) return null;
        const found = WIRE_COLORS.find(c => c.name === color);
        if (found) return found;
        // Custom color entry
        if (color === 'Other') {
            const customName = ppCustomColors[terminal];
            return { name: customName && customName.trim() ? customName.trim() : 'custom', hex: '#999999' };
        }
        return null;
    };

    const allColorsSelected = () => {
        const terminals = getPpTerminals();
        return terminals.every(t => ppWireColors[t]);
    };

    const getWireColorForDisplay = (colorHex, isDarkMode) => {
        if (!colorHex) return {};
        // Handle white color in dark/light mode
        if (colorHex === '#FFFFFF') {
            return { color: isDarkMode ? '#FFFFFF' : '#333333' };
        }
        // Handle black color in dark/light mode
        if (colorHex === '#333333') {
            return { color: isDarkMode ? '#FFFFFF' : '#333333' };
        }
        return { color: colorHex };
    };

    // ── Shared render helpers ────────────────────────────────────────────────
    const renderContactSupportContinue = (onContinue) => (
        <Box className={classes.buttonContainer}>
            <Button className={classes.yesNoButton} onClick={() => openURL(IQ_AIR_CONTACT_SUPPORT)}>
                {I18n.t('mobile.AirDeviceProvisioning.contactSupport', { defaultValue: 'Contact Support' })}
            </Button>
            <Button className={classes.primaryButton} onClick={onContinue}>
                {I18n.t('mobile.common.continue')}
            </Button>
        </Box>
    );

    const renderSimpleScreen = (icon, titleKey, titleDefault, bodyKey, bodyDefault, onContinue) => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage(icon)}
                <Typography className={classes.title}>
                    {I18n.t(titleKey, { defaultValue: titleDefault })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t(bodyKey, { defaultValue: bodyDefault })}
                </Typography>
            </Box>
            {renderBottomContinue(onContinue)}
        </>
    );

    const PP_CHIP_STYLE = { width: '80px', minWidth: '80px', height: '24px', lineHeight: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

    // ── Wire color picker ──────────────────────────────────────────────────────
    const renderColorPicker = (terminal) => {
        const selected = getColorForTerminal(terminal);
        const isOpen = openDropdown === terminal;
        const isCustom = ppWireColors[terminal] === 'Other';

        return (
            <Box className={classes.ppColorDropdown} onClick={() => setOpenDropdown(isOpen ? null : terminal)}>
                {selected ? (
                    <>
                        <span className={classes.ppColorDot} style={{ backgroundColor: selected.hex }} />
                        <Typography className={classes.ppColorName}>{selected.name}</Typography>
                    </>
                ) : (
                    <Typography className={classes.ppColorPlaceholder}>
                        {I18n.t('mobile.AirDeviceProvisioning.selectColor', { defaultValue: 'Select Color' })}
                    </Typography>
                )}
                <span className={classes.ppColorArrow}>▼</span>
                {isOpen && (
                    <Box className={classes.ppColorMenu} onClick={(e) => e.stopPropagation()}>
                        {WIRE_COLORS.map(color => (
                            <Box
                                key={color.name}
                                className={classes.ppColorOption}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPpWireColors(prev => ({ ...prev, [terminal]: color.name }));
                                    setOpenDropdown(null);
                                }}
                            >
                                <span className={classes.ppColorDot} style={{ backgroundColor: color.hex }} />
                                <Typography className={classes.ppColorOptionText}>{color.name}</Typography>
                            </Box>
                        ))}
                        <Box className={classes.ppColorOption} onClick={(e) => {
                            e.stopPropagation();
                            setPpWireColors(prev => ({ ...prev, [terminal]: 'Other' }));
                        }}>
                            <Typography className={classes.ppColorOptionText}>
                                {I18n.t('mobile.AirDeviceProvisioning.otherColor', { defaultValue: 'Other' })}
                            </Typography>
                        </Box>
                        {isCustom && (
                            <Box style={{ padding: '8px 12px' }} onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="text"
                                    placeholder={I18n.t('mobile.AirDeviceProvisioning.enterColor', { defaultValue: 'Enter color name' })}
                                    value={ppCustomColors[terminal] || ''}
                                    onChange={(e) => setPpCustomColors(prev => ({ ...prev, [terminal]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setOpenDropdown(null); }}
                                    style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', outline: 'none' }}
                                    autoFocus
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    // ══════════════════════════════════════════════════════════════════════════
    // EXISTING UW (Unused Wire) SCREENS — moved from InstallationGuide.js
    // ══════════════════════════════════════════════════════════════════════════

    const renderPowerPlate = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igUnusedWire')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.powerPlateTitle', { defaultValue: 'Do you see an unused wire?' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.missedWireNote', { defaultValue: "Look at the wire bundle behind your thermostat for a wire that isn't connected to any terminal. It may be tucked into the wall opening, wrapped around the cable, cut short, folded back, or taped off." })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.missedWireNote2', { defaultValue: 'Look carefully — unused wires are easy to miss.' })}
                </Typography>
            </Box>
            {renderYesNo(
                () => goTo(isDualRNoC ? STEPS.UNCOMMON_WIRING : PP_STEPS.PP_REQUIRED),
                () => goTo(STEPS.UW_TAKE_PHOTO)
            )}
        </>
    );

    // ── UW2: Take a photo of the unused wire ─────────────────────────────────
    const renderUwTakePhoto = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgUwTakePhoto')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwTakePhotoTitle', { defaultValue: 'Take a photo of the unused wire' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwTakePhotoBody', { defaultValue: 'Take a close-up so the color is clearly visible.' })}
                </Typography>
                {!unusedWirePhoto && (
                    <Box className={classes.photoHintBox}>
                        <Typography className={classes.photoHintText}>
                            {I18n.t('mobile.AirDeviceProvisioning.uwTakePhotoHint', { defaultValue: 'It will help you find the same wire at your HVAC system.' })}
                        </Typography>
                        <Box className={classes.photoButtonsContainer}>
                            <Button className={classes.takePhotoBtn} onClick={() => capturePhoto('unusedWire')}>
                                {I18n.t('mobile.AirDeviceProvisioning.takePhotoBtn', { defaultValue: 'TAKE PHOTO' })}
                            </Button>
                            <Button className={classes.takePhotoBtn} onClick={() => uploadPhoto('unusedWire')}>
                                {I18n.t('mobile.AirDeviceProvisioning.uploadBtn', { defaultValue: 'UPLOAD' })}
                            </Button>
                        </Box>
                    </Box>
                )}
                {unusedWirePhoto && (
                    <Box className={classes.capturedThumb}>
                        <Box className={classes.capturedThumbRow}>
                            <Typography className={classes.capturedThumbName}>
                                {unusedWirePhotoName?.substring(0, 5) + unusedWirePhotoName?.substring(unusedWirePhotoName.lastIndexOf('.'))}
                            </Typography>
                            <Button className={classes.closeThumbBtn} onClick={() => { setUnusedWirePhoto(null); setUnusedWirePhotoName(null); }}>✕</Button>
                        </Box>
                        <img src={unusedWirePhoto} alt="unused wire" className={classes.capturedThumbImg} />
                    </Box>
                )}
                <input
                    id="ig-capture-unusedWire"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect('unusedWire', e)}
                />
                <input
                    id="ig-upload-unusedWire"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect('unusedWire', e)}
                />
            </Box>
            {renderBottomContinue(() => goTo(STEPS.UW_GOTO_HVAC), I18n.t('mobile.common.continue'), !unusedWirePhoto)}
        </>
    );

    // ── UW3: Go to your HVAC unit ─────────────────────────────────────────────
    const renderUwGotoHvac = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgGotoHvac')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwGotoHvacTitle', { defaultValue: 'Go to your HVAC unit' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwGotoHvacBody', { defaultValue: "You'll need a screwdriver. Your HVAC unit is usually in the basement, garage, or a utility closet." })}
                </Typography>
                <Box className={classes.warningBanner}>
                    <Typography className={classes.warningBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.uwGotoHvacWarning1', { defaultValue: 'This step involves working inside your HVAC unit.' })}
                    </Typography>
                    <Typography className={classes.warningBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.uwGotoHvacWarning2', { defaultValue: 'We recommend an HVAC professional for this.' })}
                    </Typography>
                </Box>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.UW_OPEN_HVAC))}
        </>
    );

    // ── UW4 / PP12: Open the HVAC unit (shared) ───────────────────────────────
    const renderOpenHvacScreen = (nextStep) => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgOpenHvac')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwOpenHvacTitle', { defaultValue: 'Open the HVAC unit' })}
                </Typography>
                {I18n.t('mobile.AirDeviceProvisioning.uwOpenHvacBody', { defaultValue: 'Remove the front panel to reach the control board./n Most panels slide, unscrew, or unclip — check the sides and bottom for screws.' })
                    .split('/n').map((text, i) => (
                        <Typography key={i} className={classes.subtitle} style={i > 0 ? { marginTop: 0 } : undefined}>{text.trim()}</Typography>
                    ))}
                <Box className={classes.warningBanner}>
                    <Typography className={classes.warningBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.uwOpenHvacWarning', { defaultValue: 'Confirm HVAC power is still off at the breaker before opening the panel.' })}
                    </Typography>
                </Box>
            </Box>
            {renderBottomContinue(() => goTo(nextStep))}
        </>
    );
    const renderUwOpenHvac = () => renderOpenHvacScreen(STEPS.UW_TERMINAL_STRIP);

    // ── UW5 / PP13: Find the terminal strip (shared) ──────────────────────────
    const renderTerminalStripScreen = (nextStep) => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgTerminalStrip')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwTerminalStripTitle', { defaultValue: 'Find the terminal strip' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwTerminalStripBody', { defaultValue: 'Locate the row of screw terminals where the thermostat wires connect.' })}
                </Typography>
                <Box className={classes.infoBanner}>
                    <Typography className={classes.infoBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.uwTerminalStripInfo', { defaultValue: 'The terminal labels should look familiar — they use the same or similar letters as the terminals on your old thermostat (R, C, W, Y, G, O, B, etc.).' })}
                    </Typography>
                </Box>
            </Box>
            {renderBottomContinue(() => goTo(nextStep))}
        </>
    );
    const renderUwTerminalStrip = () => renderTerminalStripScreen(STEPS.UW_FIND_WIRE_COLOR);

    // ── UW6: Find the wire with the same color ────────────────────────────────
    const renderUwFindWireColor = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {unusedWirePhoto
                    ? <img src={unusedWirePhoto} alt="unused wire" className={classes.capturedPhotoConstrained} onClick={() => openPhoto(unusedWirePhoto)} />
                    : renderImage('IgUwTakePhoto')
                }
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwFindWireColorTitle', { defaultValue: 'Find the wire with the same color' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwFindWireColorBody', { defaultValue: 'Look for a wire at the control board that matches the color of the unused wire from your photo. The matching wire may be connected to the control board, or it may be loose, or tucked away.' })}
                </Typography>
                <Typography className={classes.subtitleLight}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwFindWireColorSupportNote', { defaultValue: "If you can't find a wire with the same color, contact our support team for help." })}
                </Typography>
            </Box>
            {renderContactSupportContinue(() => goTo(STEPS.UW_COMMON_TERMINAL))}
        </>
    );

    // ── UW7 / UW7b: Connect wire to common terminal (shared) ─────────────────
    const renderUwCommonTerminalScreen = (icon, nextStep) => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage(icon)}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwCommonTerminalTitle', { defaultValue: 'Connect the wire to the common terminal' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwCommonTerminalBody', { defaultValue: "It's usually labeled C, but may also be COM, X, or B. If unsure, check the wiring diagram, often printed inside the panel." })}
                </Typography>
                <Typography className={classes.subtitleLight}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwCommonTerminalSupportNote', { defaultValue: "If you're still unsure, contact our support team to guide you through the rest of the installation." })}
                </Typography>
            </Box>
            {renderContactSupportContinue(() => goTo(nextStep))}
        </>
    );
    const renderUwCommonTerminal = () => renderUwCommonTerminalScreen('IgConnectWire1', STEPS.UW_COMMON_TERMINAL_2);
    const renderUwCommonTerminal2 = () => renderUwCommonTerminalScreen('IgConnectWire2', STEPS.UW_CLOSE_RETURN);

    // ── UW8: Close the panel and return to your thermostat ───────────────────
    const renderUwCloseReturn = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgClosePanel')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwCloseReturnTitle', { defaultValue: 'Close the panel and return to your thermostat' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.uwCloseReturnBody', { defaultValue: "Secure the front panel and go back to the thermostat at the wall. The wire you just connected to the common terminal is now your C wire — you'll connect it to the C terminal on the base in the next steps." })}
                </Typography>
                <Box className={classes.infoBanner}>
                    <Typography className={classes.infoBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.uwCloseReturnInfo', { defaultValue: 'Ensure the front panel is fully closed — otherwise the control board may not power on.' })}
                    </Typography>
                </Box>
            </Box>
            {renderBottomContinue(handleUnusedWireComplete)}
        </>
    );

    // ══════════════════════════════════════════════════════════════════════════
    // NEW POWER PLATE (PP) SCREENS
    // ══════════════════════════════════════════════════════════════════════════

    // ── PP1: Power Plate required ─────────────────────────────────────────────
    const renderPpRequired = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igPowerPlate')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppRequiredTitle', { defaultValue: 'Power Plate required' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppRequiredBody', { defaultValue: 'Your wiring configuration requires the Power Plate. Do you have the Power Plate handy?' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppRequiredNote', { defaultValue: 'The Power Plate is sold separately.' })}
                </Typography>
            </Box>
            {renderYesNo(
                () => goTo(PP_STEPS.PP_WHAT_IS),
                () => { setIsPowerPlateFlow(true); goTo(PP_STEPS.PP_INSTALLING); }
            )}
        </>
    );

    // ── PP2.1: What is the Power Plate? ───────────────────────────────────────
    const renderPpWhatIs = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igPowerPlate')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppWhatIsTitle', { defaultValue: 'What is the Power Plate?' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppWhatIsBody', { defaultValue: 'The Power Plate enables IQ Air installation in locations where the existing wire bundle does not include a dedicated C-wire.' })}
                </Typography>
            </Box>
            {renderBottomContinue(
                () => openURL('https://enphase.com/store/thermostats/enphase-air-power-plate'),
                I18n.t('mobile.AirDeviceProvisioning.buyNow', { defaultValue: 'Buy Now' })
            )}
        </>
    );

    // ── PP2.2: Installing the Power Plate ─────────────────────────────────────
    const renderPpInstalling = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igInstalling')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppInstallingTitle', { defaultValue: 'Installing the Power Plate' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppInstallingBody', { defaultValue: 'Here is how it works:' })}
                </Typography>
                <ol className={classes.numberedList}>
                    <li><strong>{I18n.t('mobile.AirDeviceProvisioning.ppInstallingStep1Label', { defaultValue: 'At the wall' })}</strong> — {I18n.t('mobile.AirDeviceProvisioning.ppInstallingStep1', { defaultValue: 'Install the Power Plate at your old thermostat location, connecting only R and C. Attach the IQ Air display on top.' })}</li>
                    <li><strong>{I18n.t('mobile.AirDeviceProvisioning.ppInstallingStep2Label', { defaultValue: 'At the HVAC' })}</strong> — {I18n.t('mobile.AirDeviceProvisioning.ppInstallingStep2', { defaultValue: 'Install the IQ Air base next to your HVAC system and run a new cable between base and HVAC control board.' })}</li>
                    <li><strong>{I18n.t('mobile.AirDeviceProvisioning.ppInstallingStep3Label', { defaultValue: 'Wireless link' })}</strong> — {I18n.t('mobile.AirDeviceProvisioning.ppInstallingStep3', { defaultValue: 'The Power Plate and IQ Air base communicate wirelessly, giving you seamless control without additional wiring between them.' })}</li>
                </ol>
            </Box>
            <Box className={classes.buttonContainer}>
                <Button className={classes.primaryButton} onClick={() => goTo(PP_STEPS.PP_WHATS_IN_BOX)}>
                    {I18n.t('mobile.AirDeviceProvisioning.startInstallation', { defaultValue: 'Start Installation' })}
                </Button>
                <Button className={classes.secondaryButton} onClick={() => goTo(STEPS.ON_SCREEN_INSTRUCTIONS)}>
                    {I18n.t('mobile.AirDeviceProvisioning.alreadyInstalled', { defaultValue: 'Already Installed?' })}
                </Button>
            </Box>
        </>
    );

    // ── PP3: What's in the box ────────────────────────────────────────────────
    const renderPpWhatsInBox = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igPpWhatsInBox')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppWhatsInBoxTitle', { defaultValue: "What's in the box" })}
                </Typography>
                <ol className={classes.numberedList}>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppBox1', { defaultValue: 'Power Plate' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppBox2', { defaultValue: 'Cover plate' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppBox3', { defaultValue: 'Drywall anchors × 2' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppBox4', { defaultValue: 'Wall screws × 2' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppBox5', { defaultValue: 'Wire labels (R and C)' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppBox6', { defaultValue: 'Wire connectors' })}</li>
                </ol>
            </Box>
            {renderBottomContinue(() => goTo(PP_STEPS.PP_TOOLS_NEEDED))}
        </>
    );

    // ── PP4: Tools needed ─────────────────────────────────────────────────────
    const renderPpToolsNeeded = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igPpToolsNeeded')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppToolsTitle', { defaultValue: 'Tools needed' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppToolsBody', { defaultValue: 'Have these tools ready for installation:' })}
                </Typography>
                <ol className={classes.numberedList}>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppTool1', { defaultValue: 'Phillips screwdriver' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppTool2', { defaultValue: 'Pencil to mark mounting location' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppTool3', { defaultValue: 'Power drill with 3/16" drill bit' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppTool4', { defaultValue: 'Needle-nose pliers' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppTool5', { defaultValue: 'Phone with camera and internet access' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppTool6', { defaultValue: 'Thermostat cable (18 AWG, multi-conductor)' })}</li>
                </ol>
            </Box>
            {renderBottomContinue(() => goTo(PP_STEPS.PP_SELECT_COLORS))}
        </>
    );

    // ── PP5: Select your wire colors ──────────────────────────────────────────
    const renderPpSelectColors = () => {
        const terminals = getPpTerminals();
        return (
            <>
                <Box className={classes.body}>
                    {renderStepper()}
                    {renderImage('igPpSelectColors')}
                    <Typography className={classes.title}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppSelectColorsTitle', { defaultValue: 'Select your wire colors' })}
                    </Typography>
                    <Typography className={classes.subtitle}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppSelectColorsBody', { defaultValue: "Select the color of the following wires. We'll use this to guide you through the next steps." })}
                    </Typography>
                    <Box className={classes.ppColorTable}>
                        <Box className={classes.ppColorTableHeader}>
                            <Typography className={classes.ppColorColLabel}>
                                {I18n.t('mobile.AirDeviceProvisioning.terminal', { defaultValue: 'Terminal' })}
                            </Typography>
                            <Typography className={classes.ppColorColLabel}>
                                {I18n.t('mobile.AirDeviceProvisioning.wireColor', { defaultValue: 'Wire Color' })}
                            </Typography>
                        </Box>
                        {terminals.map(terminal => (
                            <Box key={terminal} className={classes.ppColorRow}>
                                <Typography className={classes.ppColorTerminal}>{terminal}</Typography>
                                {renderColorPicker(terminal)}
                            </Box>
                        ))}
                    </Box>
                </Box>
                {renderBottomContinue(
                    () => goTo(PP_STEPS.PP_REMOVE_THERMOSTAT),
                    I18n.t('mobile.common.continue'),
                    !allColorsSelected()
                )}
            </>
        );
    };

    // ── PP: Remove the old thermostat ─────────────────────────────────────────
    const renderPpRemoveThermostat = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igRemoveThermostat')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.removeThermostatTitle', { defaultValue: 'Remove the old thermostat' })}
                </Typography>
                <ol className={classes.numberedList}>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppRemoveStep1', { defaultValue: 'Loose each terminal and carefully disconnect each wire.' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ppRemoveStep2', { defaultValue: 'Uninstall the thermostat base from the wall.' })}</li>
                </ol>
            </Box>
            {renderBottomContinue(() => goTo(PP_STEPS.PP_INSTALL_TRIM))}
        </>
    );

    // ── PP7: Optional: Install the trim plate ─────────────────────────────────
    const renderPpInstallTrim = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igTrimPlate')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppInstallTrimTitle', { defaultValue: 'Optional: Install the trim plate' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppInstallTrimBody', { defaultValue: 'If your wall has marks or holes from the old thermostat, attach the trim plate(1) to cover them before mounting.' })}
                </Typography>
            </Box>
            {renderBottomContinue(() => goTo(PP_STEPS.PP_MOUNT_PLATE))}
        </>
    );

    // ── PP8: Mount the Power Plate to the wall ────────────────────────────────
    const renderPpMountPlate = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igPpMountPlate')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppMountPlateTitle', { defaultValue: 'Mount the Power Plate to the wall' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppMountPlateBody', { defaultValue: 'If needed, drill two holes with a 3/16" bit and insert the drywall anchors before securing the Power Plate.' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppMountPlateNote', { defaultValue: 'Make sure you are using the Power Plate from the Power Plate box. It has only two terminals: R and C.' })}
                </Typography>
            </Box>
            {renderBottomContinue(() => goTo(allColorsSelected() ? PP_STEPS.PP_CONNECT_WIRES : PP_STEPS.PP_SELECT_COLORS))}
        </>
    );

    // ── PP9: Connect wires to the Power Plate ─────────────────────────────────
    const renderPpConnectWires = () => {
        const terminals = getPpTerminals();
        const ppMap = [];
        if (terminals.length > 0) {
            const rTerminal = terminals[0];
            const rColor = getColorForTerminal(rTerminal);
            ppMap.push({ old: `${rColor?.name?.toLowerCase() || '[color]'} (${rTerminal})`, 'new': 'R', colorHex: rColor?.hex || null });
        }
        if (terminals.length > 1) {
            const cTerminal = terminals[1];
            const cColor = getColorForTerminal(cTerminal);
            ppMap.push({ old: `${cColor?.name?.toLowerCase() || '[color]'} (${cTerminal})`, 'new': 'C', colorHex: cColor?.hex || null });
        }

        return (
            <>
                <Box className={classes.body}>
                    {renderStepper()}
                    {renderImage('igPpConnectWires', isDarkMode)}
                    <Typography className={classes.title}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppConnectWiresTitle', { defaultValue: 'Connect wires to the Power Plate' })}
                    </Typography>
                    <Typography className={classes.subtitle}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppConnectWiresBody', { defaultValue: 'Connect the following wires to the R and C terminal. After connecting, apply the included wire labels.' })}
                    </Typography>
                    <Box className={classes.wiringTable}>
                        <Box className={`${classes.wiringTableHeader} ${isDarkMode ? classes.wiringArrowDarkColor : ''}`} style={{ gap: '16px', marginLeft: 0, alignItems: 'center' }}>
                            <Typography className={classes.wiringColLabel} style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>
                                {I18n.t('mobile.AirDeviceProvisioning.wire', { defaultValue: 'Wire' })}
                            </Typography>
                            <span className={classes.wiringArrow}><Icon src='IgRightArrow' /></span>
                            <Typography className={classes.wiringColLabel} style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>
                                {I18n.t('mobile.AirDeviceProvisioning.terminal', { defaultValue: 'Terminal' })}
                            </Typography>
                        </Box>
                        {ppMap.map((row, i) => (
                            <Box key={i} className={`${classes.wiringRow} ${isDarkMode ? classes.wiringArrowDarkColor : ''}`} style={{ gap: '16px' }}>
                                <span className={classes.wiringChipOld} style={{ ...PP_CHIP_STYLE, ...getWireColorForDisplay(row.colorHex, isDarkMode) }}>{row.old}</span>
                                <span className={classes.wiringArrow}><Icon src='IgRightArrow' /></span>
                                <span className={classes.wiringChipNew} style={PP_CHIP_STYLE}>{row['new']}</span>
                            </Box>
                        ))}
                    </Box>
                </Box>
                {renderBottomContinue(() => goTo(PP_STEPS.PP_GOTO_HVAC))}
            </>
        );
    };

    // ── PP11: Go to your HVAC unit ────────────────────────────────────────────
    const renderPpGotoHvac = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgGotoHvac')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppGotoHvacTitle', { defaultValue: 'Go to your HVAC unit' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppGotoHvacBody', { defaultValue: 'Bring the remaining equipment and tools with you, especially a screwdriver and cable. Your HVAC unit is usually in the basement, garage, or a utility closet.' })}
                </Typography>
                <Box className={classes.warningBanner}>
                    <Typography className={classes.warningBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppGotoHvacWarning1', { defaultValue: 'This step involves working inside your HVAC unit.' })}
                    </Typography>
                    <Typography className={classes.warningBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppGotoHvacWarning2', { defaultValue: 'We recommend an HVAC professional for this.' })}
                    </Typography>
                </Box>
            </Box>
            {renderBottomContinue(() => goTo(PP_STEPS.PP_OPEN_HVAC))}
        </>
    );

    // ── PP12 / PP13: Reuse shared Open HVAC and Terminal Strip screens ───────
    const renderPpOpenHvac = () => renderOpenHvacScreen(PP_STEPS.PP_TERMINAL_STRIP);
    const renderPpTerminalStrip = () => renderTerminalStripScreen(PP_STEPS.PP_CONNECT_C);

    // ── PP14: Connect C at the control board (steps 1-2) ──────────────────────
    const renderPpConnectC = () => {
        const terminals = getPpTerminals();
        const secondTerminal = terminals.length > 1 ? terminals[1] : 'Y';
        const secondColor = getColorForTerminal(secondTerminal);
        const colorName = secondColor ? secondColor.name.toLowerCase() : '';

        return (
            <>
                <Box className={classes.body}>
                    {renderStepper()}
                    {renderImage('igPpConnectC')}
                    <Typography className={classes.title}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppConnectCTitle', { defaultValue: 'Connect C at the control board' })}
                    </Typography>
                    <ol className={classes.numberedList}>
                        <li>{I18n.t('mobile.AirDeviceProvisioning.ppConnectCStep1', { defaultValue: 'Remove the {{color}} wire from the terminal and connect it to the common terminal — C, COM, X, or B.', color: colorName })}</li>
                        <li>{I18n.t('mobile.AirDeviceProvisioning.ppConnectCStep2', { defaultValue: 'If there is already a wire on the C terminal from your accessory (humidifier, vent, etc.), leave it connected. Both wires share the C terminal.' })}</li>
                    </ol>
                </Box>
                <Typography className={classes.subtitleLight} style={{ marginBottom: '8px' }}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppConnectCSupportNote', { defaultValue: "If you're unsure, contact our support team to guide you through the rest of the installation." })}
                </Typography>
                {renderContactSupportContinue(() => goTo(PP_STEPS.PP_CONNECT_C_2))}
            </>
        );
    };

    // ── PP14b: Connect C at the control board (steps 3-4) ─────────────────────
    const renderPpConnectC2 = () => {
        const terminals = getPpTerminals();
        const rTerminal = terminals.length > 0 ? terminals[0] : 'R';
        const rColor = getColorForTerminal(rTerminal);
        const colorName = rColor ? rColor.name.toLowerCase() : '';

        return (
            <>
                <Box className={classes.body}>
                    {renderStepper()}
                    {renderImage('igPpConnectC2')}
                    <Typography className={classes.title}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppConnectCTitle', { defaultValue: 'Connect C at the control board' })}
                    </Typography>
                    <ol className={classes.numberedList} start="3">
                        <li>{I18n.t('mobile.AirDeviceProvisioning.ppConnectCStep3', { defaultValue: 'Leave the {{color}} wire on the R terminal.', color: colorName })}</li>
                        <li>{I18n.t('mobile.AirDeviceProvisioning.ppConnectCStep4', { defaultValue: 'Disconnect all other wires and wrap them around the bundle.' })}</li>
                    </ol>
                </Box>
                <Typography className={classes.subtitleLight} style={{ marginBottom: '8px' }}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppConnectCSupportNote', { defaultValue: "If you're unsure, contact our support team to guide you through the rest of the installation." })}
                </Typography>
                {renderContactSupportContinue(() => goTo(PP_STEPS.PP_MOUNT_BASE))}
            </>
        );
    };

    // ── PP15: Mount the IQ Air base ───────────────────────────────────────────
    const renderPpMountBase = () => renderSimpleScreen(
        'igPpMountBase',
        'mobile.AirDeviceProvisioning.ppMountBaseTitle', 'Mount the IQ Air base',
        'mobile.AirDeviceProvisioning.ppMountBaseBody', 'Level and mount the IQ Air base near the HVAC unit on any non-metal surface to ensure a good wireless connection to the IQ Air display.',
        () => goTo(PP_STEPS.PP_INSTALL_CABLE)
    );

    // ── PP16: Install new thermostat cable ────────────────────────────────────
    const renderPpInstallCable = () => {
        const ppWiringMap = ppWiringMapState;

        return (
            <>
                <Box className={classes.body}>
                    {renderStepper()}
                    {renderImage('igPpInstallCable')}
                    <Typography className={classes.title}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppInstallCableTitle', { defaultValue: 'Install new thermostat cable between control board and IQ Air base' })}
                    </Typography>
                    <Typography className={classes.subtitle}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppInstallCableBody', { defaultValue: 'Run one wire for each terminal shown in the wiring map below. Insert wires into the base terminals from the front-' })}
                    </Typography>
                    {ppWiringMap.length > 0 && (
                        <Box className={classes.wiringTable}>
                            <Box className={`${classes.wiringTableHeader} ${isDarkMode ? classes.wiringArrowDarkColor : ''}`} style={{ gap: '30px' }}>
                                <Typography className={classes.wiringColLabel} style={{ minWidth: '80px' }}>
                                    {I18n.t('mobile.AirDeviceProvisioning.controlBoard', { defaultValue: 'Control board' })}
                                </Typography>
                                <span className={classes.wiringArrow}><Icon src='IgRightArrow' /></span>
                                <Typography className={classes.wiringColLabel} style={{ minWidth: '80px' }}>
                                    {I18n.t('mobile.AirDeviceProvisioning.iqAirTerminal', { defaultValue: 'IQ Air terminal' })}
                                </Typography>
                            </Box>
                            {ppWiringMap.filter(row => row.action === 'connect').map((row, i) => (
                                <Box key={i} className={`${classes.wiringRow} ${isDarkMode ? classes.wiringArrowDarkColor : ''}`} style={{ gap: '30px' }}>
                                    <span className={classes.wiringChipOld} style={{ minWidth: '80px' }}>{row.old}</span>
                                    <span className={classes.wiringArrow}><Icon src='IgRightArrow' /></span>
                                    <span className={classes.wiringChipNew} style={{ minWidth: '80px' }}>{row['new']}</span>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                {renderBottomContinue(() => goTo(PP_STEPS.PP_CLOSE_PANEL))}
            </>
        );
    };

    // ── PP17: Close the front panel ───────────────────────────────────────────
    const renderPpClosePanel = () => renderSimpleScreen(
        'IgClosePanel',
        'mobile.AirDeviceProvisioning.ppClosePanelTitle', 'Close the front panel',
        'mobile.AirDeviceProvisioning.ppClosePanelBody', 'Ensure the front panel is fully closed — otherwise the control board may not power on.',
        () => goTo(STEPS.RESTORE_POWER)
    );

    // ── PP18: Attach the cover plate ──────────────────────────────────────────
    const renderPpAttachCover = () => renderSimpleScreen(
        'igPpAttachCover',
        'mobile.AirDeviceProvisioning.ppAttachCoverTitle', 'Attach the cover plate',
        'mobile.AirDeviceProvisioning.ppAttachCoverBody', 'Align the cover plate with the base, and magnets will pull the cover plate into place.',
        () => goTo(PP_STEPS.PP_RETURN_PLATE)
    );

    // ── PP19: Return to the Power Plate ───────────────────────────────────────
    const renderPpReturnPlate = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igPpReturnPlate')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppReturnPlateTitle', { defaultValue: 'Return to the Power Plate' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ppReturnPlateBody', { defaultValue: 'Return to the Power Plate, attach the display and verify it has powered on and is prompting setup.' })}
                </Typography>
                <Box className={classes.infoBanner}>
                    <Typography className={classes.ppReturnBulletItem} style={{ paddingLeft: 0 }}>
                        {I18n.t('mobile.AirDeviceProvisioning.ppReturnPlateInfoTitle', { defaultValue: 'If the Display does not power on after 30 seconds:' })}
                    </Typography>
                    <Box className={classes.ppReturnBulletList}>
                        <Typography className={classes.ppReturnBulletItem}>
                            {I18n.t('mobile.AirDeviceProvisioning.ppReturnPlateTip1', { defaultValue: 'Verify both R and C wires are securely inserted into the appropriate terminals on the Power Plate.' })}
                        </Typography>
                        <Typography className={classes.ppReturnBulletItem}>
                            {I18n.t('mobile.AirDeviceProvisioning.ppReturnPlateTip2', { defaultValue: 'Verify the status LED is lighting up.' })}
                        </Typography>
                        <Typography className={classes.ppReturnBulletItem}>
                            {I18n.t('mobile.AirDeviceProvisioning.ppReturnPlateTip3', { defaultValue: 'Verify the HVAC unit has power.' })}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            {renderContactSupportContinue(() => goTo(STEPS.ON_SCREEN_INSTRUCTIONS))}
        </>
    );

    // ══════════════════════════════════════════════════════════════════════════
    // Screen dispatch
    // ══════════════════════════════════════════════════════════════════════════
    const renderContent = () => {
        switch (currentStep) {
            case STEPS.POWER_PLATE: return renderPowerPlate();
            case STEPS.UW_TAKE_PHOTO: return renderUwTakePhoto();
            case STEPS.UW_GOTO_HVAC: return renderUwGotoHvac();
            case STEPS.UW_OPEN_HVAC: return renderUwOpenHvac();
            case STEPS.UW_TERMINAL_STRIP: return renderUwTerminalStrip();
            case STEPS.UW_FIND_WIRE_COLOR: return renderUwFindWireColor();
            case STEPS.UW_COMMON_TERMINAL: return renderUwCommonTerminal();
            case STEPS.UW_COMMON_TERMINAL_2: return renderUwCommonTerminal2();
            case STEPS.UW_CLOSE_RETURN: return renderUwCloseReturn();
            case PP_STEPS.PP_REQUIRED: return renderPpRequired();
            case PP_STEPS.PP_WHAT_IS: return renderPpWhatIs();
            case PP_STEPS.PP_INSTALLING: return renderPpInstalling();
            case PP_STEPS.PP_WHATS_IN_BOX: return renderPpWhatsInBox();
            case PP_STEPS.PP_TOOLS_NEEDED: return renderPpToolsNeeded();
            case PP_STEPS.PP_SELECT_COLORS: return renderPpSelectColors();
            case PP_STEPS.PP_REMOVE_THERMOSTAT: return renderPpRemoveThermostat();
            case PP_STEPS.PP_INSTALL_TRIM: return renderPpInstallTrim();
            case PP_STEPS.PP_MOUNT_PLATE: return renderPpMountPlate();
            case PP_STEPS.PP_CONNECT_WIRES: return renderPpConnectWires();
            case PP_STEPS.PP_GOTO_HVAC: return renderPpGotoHvac();
            case PP_STEPS.PP_OPEN_HVAC: return renderPpOpenHvac();
            case PP_STEPS.PP_TERMINAL_STRIP: return renderPpTerminalStrip();
            case PP_STEPS.PP_CONNECT_C: return renderPpConnectC();
            case PP_STEPS.PP_CONNECT_C_2: return renderPpConnectC2();
            case PP_STEPS.PP_MOUNT_BASE: return renderPpMountBase();
            case PP_STEPS.PP_INSTALL_CABLE: return renderPpInstallCable();
            case PP_STEPS.PP_CLOSE_PANEL: return renderPpClosePanel();
            case PP_STEPS.PP_ATTACH_COVER: return renderPpAttachCover();
            case PP_STEPS.PP_RETURN_PLATE: return renderPpReturnPlate();
            default: return null;
        }
    };

    return renderContent();
};

export default NoCWireFlow;
